import {useEffect, useMemo, useState} from 'react'
import {Alert} from 'react-bootstrap'
import Modal from '../Modal'
import InputField from '../InputField'
import Select from '../Select'
import Switch from '../Switch'
import {
  AccrualType,
  CreatePayArticleTypeModel,
  PayArticleTypeModel,
  PayArticleValueSign,
  UpdatePayArticleTypeModel,
} from '../../redux/types'

interface Props {
  error?: string | null
  initialValue?: PayArticleTypeModel
  onCancelClick?: () => void
  onSaveClick?: (model: CreatePayArticleTypeModel | UpdatePayArticleTypeModel) => void
  visible: boolean
}

interface PayArticleTypeFormValues {
  code: string
  displayName: string
  isDefaultSalary: boolean
  isDefaultStandardShift: boolean
  isDefaultCustomShift: boolean
  isDefaultPayDeviation: boolean
  availabilityRatio: string
  isShiftBasedSalaryArticle: boolean
  valueSign: PayArticleValueSign
  accrualType: AccrualType
}

const defaultFormValues: PayArticleTypeFormValues = {
  code: '',
  displayName: '',
  isDefaultSalary: false,
  isDefaultStandardShift: false,
  isDefaultCustomShift: false,
  isDefaultPayDeviation: false,
  availabilityRatio: '0',
  isShiftBasedSalaryArticle: false,
  valueSign: PayArticleValueSign.positive,
  accrualType: AccrualType.arrears
}

const EditPayArticleTypeModal = ({error, initialValue, onCancelClick, onSaveClick, visible}: Props) => {
  const isEditMode = initialValue != null
  const [formValues, setFormValues] = useState<PayArticleTypeFormValues>(defaultFormValues)

  useEffect(() => {
    if (!visible) {
      return
    }

    if (initialValue) {
      setFormValues({
        code: initialValue.code,
        displayName: initialValue.displayName,
        isDefaultSalary: initialValue.isDefaultSalary,
        isDefaultStandardShift: initialValue.isDefaultStandardShift,
        isDefaultCustomShift: initialValue.isDefaultCustomShift,
        isDefaultPayDeviation: initialValue.isDefaultPayDeviation,
        availabilityRatio: initialValue.availabilityRatio.toString(),
        isShiftBasedSalaryArticle: initialValue.isShiftBasedSalaryArticle,
        valueSign: initialValue.valueSign,
        accrualType: initialValue.accrualType,
      })
      return
    }

    setFormValues(defaultFormValues)
  }, [initialValue, visible])

  const availabilityRatio = Number(formValues.availabilityRatio)
  const availabilityRatioValid = formValues.availabilityRatio !== '' && !Number.isNaN(availabilityRatio) && availabilityRatio >= 0 && availabilityRatio <= 1
  const codeValid = isEditMode || formValues.code.trim().length > 0
  const displayNameValid = formValues.displayName.trim().length > 0

  const formValid = useMemo(() => {
    return codeValid && displayNameValid && availabilityRatioValid
  }, [availabilityRatioValid, codeValid, displayNameValid])

  const handleOnClose = () => {
    setFormValues(defaultFormValues)
    onCancelClick && onCancelClick()
  }

  const handleOnChange = (propName: keyof PayArticleTypeFormValues, value: string | boolean) => {
    setFormValues({...formValues, [propName]: value})
  }

  const handleOnSave = () => {
    if (!formValid) {
      return
    }

    if (isEditMode) {
      onSaveClick && onSaveClick({
        displayName: formValues.displayName.trim(),
        isDefaultSalary: formValues.isDefaultSalary,
        isDefaultStandardShift: formValues.isDefaultStandardShift,
        isDefaultCustomShift: formValues.isDefaultCustomShift,
        isDefaultPayDeviation: formValues.isDefaultPayDeviation,
        availabilityRatio: availabilityRatio,
        isShiftBasedSalaryArticle: formValues.isShiftBasedSalaryArticle,
        valueSign: formValues.valueSign,
        accrualType: formValues.accrualType,
      } as UpdatePayArticleTypeModel)
      return
    }

    onSaveClick && onSaveClick({
      code: formValues.code.trim(),
      displayName: formValues.displayName.trim(),
      isDefaultSalary: formValues.isDefaultSalary,
      isDefaultStandardShift: formValues.isDefaultStandardShift,
      isDefaultCustomShift: formValues.isDefaultCustomShift,
      isDefaultPayDeviation: formValues.isDefaultPayDeviation,
      availabilityRatio: availabilityRatio,
      isShiftBasedSalaryArticle: formValues.isShiftBasedSalaryArticle,
      valueSign: formValues.valueSign,
      accrualType: formValues.accrualType,
    } as CreatePayArticleTypeModel)
  }

  const valueSignOptions = Object.values(PayArticleValueSign).map((value) => {
    return {name: value, value: value}
  })

  const accrualTypeOptions = Object.values(AccrualType).map((value) => {
    return {name: value, value: value}
  })

  return (
    <Modal
      title={isEditMode ? 'Update pay article type' : 'Add pay article type'}
      show={visible}
      okButtonTitle='Save'
      cancelButtonTitle='Cancel'
      okButtonDisabled={!formValid}
      onHide={handleOnClose}
      onOkClick={handleOnSave}
      onCancelClick={handleOnClose}
    >
      {!!error && <Alert variant='danger'>{error}</Alert>}
      <InputField
        id='code'
        value={formValues.code}
        onChange={(event) => handleOnChange('code', event.target.value)}
        label='Code'
        error={!codeValid ? 'Code is required' : undefined}
        disabled={isEditMode}
        placeholder=''
      />
      <InputField
        id='displayName'
        value={formValues.displayName}
        onChange={(event) => handleOnChange('displayName', event.target.value)}
        label='Display name'
        error={!displayNameValid ? 'Display name is required' : undefined}
        placeholder=''
      />
      <Select
        id='valueSign'
        label='Value sign'
        options={valueSignOptions}
        selectedValue={formValues.valueSign}
        onSelect={(value) => handleOnChange('valueSign', value as PayArticleValueSign)}
      />
      <Select
        id='accrualType'
        label='Accrual type'
        options={accrualTypeOptions}
        selectedValue={formValues.accrualType}
        onSelect={(value) => handleOnChange('accrualType', value as AccrualType)}
      />
      <InputField
        id='availabilityRatio'
        value={formValues.availabilityRatio}
        onChange={(event) => handleOnChange('availabilityRatio', event.target.value)}
        label='Availability ratio'
        error={!availabilityRatioValid ? 'Availability ratio must be between 0 and 1' : undefined}
        numeric={true}
        placeholder=''
      />
      <Switch
        id='isShiftBasedSalaryArticle'
        checked={formValues.isShiftBasedSalaryArticle}
        label='Shift-based salary article'
        onChange={() => handleOnChange('isShiftBasedSalaryArticle', !formValues.isShiftBasedSalaryArticle)}
      />
      <Switch
        id='isDefaultSalary'
        checked={formValues.isDefaultSalary}
        label='Default salary'
        onChange={() => handleOnChange('isDefaultSalary', !formValues.isDefaultSalary)}
      />
      <Switch
        id='isDefaultStandardShift'
        checked={formValues.isDefaultStandardShift}
        label='Default standard shift'
        onChange={() => handleOnChange('isDefaultStandardShift', !formValues.isDefaultStandardShift)}
      />
      <Switch
        id='isDefaultCustomShift'
        checked={formValues.isDefaultCustomShift}
        label='Default custom shift'
        onChange={() => handleOnChange('isDefaultCustomShift', !formValues.isDefaultCustomShift)}
      />
      <Switch
        id='isDefaultPayDeviation'
        checked={formValues.isDefaultPayDeviation}
        label='Default pay deviation'
        onChange={() => handleOnChange('isDefaultPayDeviation', !formValues.isDefaultPayDeviation)}
      />
    </Modal>
  )
}

export default EditPayArticleTypeModal
